/**
 * Default configuration values for the website
 * These values are used when localStorage is empty or unavailable
 * 
 * To update these defaults with your current localhost settings:
 * 1. Run the extract-localhost-settings.js script in your browser console
 * 2. Copy the output
 * 3. Update the values below
 */

import { DisplaySettings } from '../types/Gallery';
import { FOOTER_COPY_DEFAULTS } from '../utils/localBusiness';

export const DEFAULT_WEBSITE_CONTENT = {
  studioName: 'Carmen Lorraine Photography',
  heroDescription: 'Hi, my name is Carmen and I am a lifestyle Photographer in Cape Town. I am grateful for life on this incredible planet and cherish each day! My creativity is inspired by the sights, sounds, and colours of the earth and surrounding sky and galaxies.',
  aboutDescription: 'I love people & their diversity. I love walking in the mountains, swimming in the ocean, looking up at the night sky. Dancing with wild abandon and connecting to my spirit. I love long chats and laughter with friends. I honour and love my little family:-) My daughter being the brightest light in my life.',
  contactEmail: 'hello@carmenlorraine.com',
  contactPhone: '+27 (0) 82 415 1652',
  contactLocation: 'Cape Town, South Africa',
  footerLocality: FOOTER_COPY_DEFAULTS.footerLocality as string,
  areasServed: FOOTER_COPY_DEFAULTS.areasServed as string,
  reviewLabel: FOOTER_COPY_DEFAULTS.reviewLabel as string,
  reviewUrl: FOOTER_COPY_DEFAULTS.reviewUrl as string,
  whereIShootHeading: FOOTER_COPY_DEFAULTS.whereIShootHeading as string,
  whereIShootBody: FOOTER_COPY_DEFAULTS.whereIShootBody as string,
  whereIShootLinks: (FOOTER_COPY_DEFAULTS.whereIShootLinks as { label: string; href: string }[]).map((link) => ({ ...link })),
};

export const DEFAULT_LOGO = {
  imageUrl: 'https://res.cloudinary.com/di1mzmlts/image/upload/v1767339554/carmen_lorraine_photos/logos/yk0e7dossm5jxaem7pmj.png',
  altText: 'Carmen Lorraine Photography',
  publicId: 'carmen_lorraine_photos/logos/yk0e7dossm5jxaem7pmj',
  width: 128,
  height: 125
};

export const DEFAULT_DISPLAY_SETTINGS: DisplaySettings = {
  homepageGalleryStyle: 'masonry-original',
  galleryDisplayStyle: 'masonry-large',
  galleryCount: 10,
  packageCardStyle: 'bold',
  packageCardImageHeight: 260,
  packageCardSpacing: 1,
  packageCardColumns: 3,
  packageCardBorderRadius: 26,
  packageCardShadow: 'bold',
  packageCardShowTitle: true,
  packageCardShowDescription: true,
  packageCardShowDuration: true,
  featuredPackageCount: 3,
  // Homepage Section Visibility - all enabled by default
  showGalleriesSection: true,
  showAboutSection: true,
  showServicesSection: true,
  showInstagramSection: true,
  showBlogSection: true,
  homepageBlogCount: 3,
  showContactSection: true
};

export const DEFAULT_SEO_SETTINGS = {
  defaults: {
    title: 'Carmen Lorraine Photography - Professional Photography Services',
    description: 'Professional photography services specializing in portraits, weddings, and events.',
    image: '/logo512.png',
    author: 'Carmen Lorraine',
    keywords: ['photography', 'portraits', 'weddings', 'events', 'professional photographer'],
    noIndex: false,
  },
  perPage: {}
};

// Add other defaults as needed
export const DEFAULT_CURRENCY_SETTINGS = {
  currency: 'ZAR',
  symbol: 'R',
  position: 'before' as 'before' | 'after'
};

export const DEFAULT_THEME = {
  palette: {
    primary: '#000000',
    secondary: '#333333',
    accent: '#666666',
    background: '#FFFFFF',
    text: '#000000',
    surface: '#F5F5F5',
    border: '#CCCCCC'
  },
  presetId: 'high-contrast-bw',
  lastUpdated: '2026-01-04T08:43:36.553Z'
};

export const DEFAULT_PACKAGE_CUSTOMIZATION = {
  processSteps: [
    {
      id: '1',
      number: 1,
      title: 'Initial Consultation',
      description: "I'll discuss your vision, preferences, and specific requirements to tailor the perfect session for you."
    },
    {
      id: 'step-1767381112385',
      number: 2,
      title: 'Site Recce',
      description: 'A visit to the venue prior to the shoot ......'
    },
    {
      id: 'step-1767340620651',
      number: 3,
      title: 'Consultation [in-person]',
      description: 'A personal [in-person] meeting to plan and discuss your perfect photo shoot.'
    },
    {
      id: '2',
      number: 4,
      title: 'Planning & Preparation',
      description: "I'll provide guidance on wardrobe, location, timing, and any special considerations for your session."
    },
    {
      id: '3',
      number: 5,
      title: 'Photography Session',
      description: 'Professional photography with natural posing guidance and a relaxed, enjoyable atmosphere.'
    },
    {
      id: '4',
      number: 6,
      title: 'Editing & Delivery',
      description: 'Careful editing of your images with professional retouching and delivery via online gallery.'
    }
  ],
  expectations: [
    {
      id: '1',
      icon: 'fas fa-camera',
      title: 'Professional Equipment',
      description: 'State-of-the-art camera gear and professional lighting equipment for stunning results.'
    },
    {
      id: '4',
      icon: 'fas fa-star',
      title: 'Quality Guarantee',
      description: 'Professional editing and retouching to ensure you receive only the highest quality images.'
    },
    {
      id: '3',
      icon: 'fas fa-clock',
      title: 'Flexible Timing',
      description: 'I work around your schedule to find the perfect time and lighting for your session.'
    },
    {
      id: '2',
      icon: 'fas fa-heart',
      title: 'Personal Touch',
      description: 'Every session is tailored to your unique style and personality for authentic, meaningful photos.'
    },
    {
      id: 'exp-1767021704440',
      icon: 'fas fa-medal',
      title: 'best service',
      description: ''
    },
    {
      id: 'exp-1767021705006',
      icon: 'fas fa-rocket',
      title: 'another thing',
      description: ''
    }
  ],
  testimonials: [
    {
      id: '1',
      content: 'Carmen has an incredible eye for detail and made our session so comfortable and fun. The photos exceeded our expectations!',
      author: 'Sarah M.',
      service: 'Portrait Package'
    },
    {
      id: '2',
      content: 'Professional, creative, and so easy to work with. Carmen captured our family perfectly and we\'ll treasure these photos forever.',
      author: 'John & Lisa',
      service: 'Family Package'
    },
    {
      id: '3',
      content: 'The wedding photos are absolutely stunning! Carmen captured every special moment beautifully. Highly recommended!',
      author: 'Michael & Emma',
      service: 'Wedding Package'
    }
  ],
  globalBookingNote: 'Free consultation included • Flexible scheduling • Professional results guaranteed'
};

export const DEFAULT_CURRENCY_SETTINGS_EXPANDED = {
  USD: {
    markupPercentage: 10,
    enabled: true,
    roundToNearest: 5
  },
  EUR: {
    markupPercentage: 10,
    enabled: true,
    roundToNearest: 5
  },
  GBP: {
    markupPercentage: 10,
    roundToNearest: 5,
    enabled: true
  },
  CAD: {
    markupPercentage: 10,
    roundToNearest: 5,
    enabled: true
  },
  AUD: {
    markupPercentage: 10,
    roundToNearest: 5,
    enabled: true
  }
};

